import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/SupabaseAuthContext'
import {
  getUserTokenBalance,
  getUserTokenTransactions,
  addTokensToUser,
  spendUserTokens,
  createTokenPurchase,
  recordTokenTransaction
} from '../services/tokenService'
import type { TokenTransaction } from '../types/database'

export const useTokens = () => {
  const { user } = useAuth()
  const [balance, setBalance] = useState(0)
  const [transactions, setTransactions] = useState<TokenTransaction[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Carregar saldo de tokens
  const loadBalance = useCallback(async () => {
    if (!user) return
    
    try {
      setLoading(true)
      setError(null)
      const tokenBalance = await getUserTokenBalance(user.id)
      console.log('useTokens: Carregando saldo, resultado:', tokenBalance)
      
      // Exibir o saldo real do banco de dados
      setBalance(tokenBalance)
    } catch (err) {
      setError('Erro ao carregar saldo de tokens')
      console.error('Erro ao carregar saldo:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Carregar histórico de transações
  const loadTransactions = useCallback(async (limit = 10) => {
    if (!user) return
    
    try {
      setLoading(true)
      setError(null)
      const transactionHistory = await getUserTokenTransactions(user.id, limit)
      setTransactions(transactionHistory)
    } catch (err) {
      setError('Erro ao carregar histórico de transações')
      console.error('Erro ao carregar transações:', err)
    } finally {
      setLoading(false)
    }
  }, [user])

  // Adicionar tokens
  const addUserTokens = useCallback(async (amount: number, description: string) => {
    if (!user) return false
    
    try {
      setLoading(true)
      setError(null)
      await addTokensToUser(user.id, amount, description)
      await loadBalance() // Recarregar saldo
      await loadTransactions() // Recarregar transações
      return true
    } catch (err) {
      setError('Erro ao adicionar tokens')
      console.error('Erro ao adicionar tokens:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [user, loadBalance, loadTransactions])

  // Gastar tokens
  const spendTokens = useCallback(async (amount: number, description: string) => {
    if (!user) return false
    
    try {
      setLoading(true)
      setError(null)
      console.log('useTokens: Iniciando gasto de tokens:', amount, description)
      const success = await spendUserTokens(user.id, amount, description)
      console.log('useTokens: Resultado do gasto:', success)
      if (success) {
        console.log('useTokens: Recarregando saldo após gasto...')
        await loadBalance() // Recarregar saldo
        await loadTransactions() // Recarregar transações
        console.log('useTokens: Saldo recarregado, novo balance:', balance)
      }
      return success
    } catch (err) {
      setError('Erro ao gastar tokens')
      console.error('Erro ao gastar tokens:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [user, loadBalance, loadTransactions])

  // Criar compra de tokens
  const createPurchase = useCallback(async (tokenAmount: number, reaisAmount: number) => {
    if (!user) return null
    
    try {
      setLoading(true)
      setError(null)
      const purchase = await createTokenPurchase(user.id, tokenAmount, reaisAmount)
      return purchase
    } catch (err) {
      setError('Erro ao criar compra de tokens')
      console.error('Erro ao criar compra:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [user])

  // Verificar se o usuário tem tokens suficientes
  const hasEnoughTokens = useCallback((amount: number) => {
    return balance >= amount
  }, [balance])

  // Atualizar saldo localmente (para responsividade da UI)
  const updateBalanceLocally = useCallback((newBalance: number) => {
    console.log(`💰 [useTokens] Atualizando saldo localmente: ${balance} -> ${newBalance}`);
    setBalance(newBalance);
  }, [balance])

  // Carregar dados iniciais - apenas o saldo por padrão
  useEffect(() => {
    if (user) {
      loadBalance()
      // Não carregamos transações automaticamente para evitar requisições desnecessárias
      // loadTransactions() 
    }
  }, [user, loadBalance])

  return {
    balance,
    transactions,
    loading,
    error,
    loadBalance,
    loadTransactions,
    addUserTokens,
    spendTokens,
    createPurchase,
    hasEnoughTokens,
    updateBalanceLocally,
    refreshData: async () => {
      await loadBalance()
      await loadTransactions()
    }
  }
}

export default useTokens